import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Api } from '../api/client.js';
import type { AuthClient } from '../lib/auth.js';
import type { Mfa } from '../lib/mfa.js';

export interface Session {
  logout: () => void;
  /** Neue Tokens holen (nach MFA-Einrichtung, damit die Admin-Gruppe im Token erscheint). */
  refresh: () => Promise<void>;
  mock: boolean;
  /** Pilotbetrieb: Hinweis, dass alle Daten vor dem Start gelöscht werden. */
  pilot?: boolean;
}

interface Ctx {
  api: Api;
  mfa: Mfa;
  auth: AuthClient;
  session: Session;
}

const AppContext = createContext<Ctx | null>(null);

export function AppProvider({ children, ...value }: Ctx & { children: ReactNode }) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): Ctx {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('AppProvider fehlt');
  return ctx;
}

export function useMe() {
  const { api } = useApp();
  return useQuery({ queryKey: ['me'], queryFn: () => api.me() });
}

const desktopQuery = '(min-width: 1024px)';
export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia(desktopQuery);
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia(desktopQuery).matches,
  );
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = `${title} · Betriebsam`;
  }, [title]);
}
